/**
 * RealtimeContext — Provides a shared WebSocket connection for staff dashboards.
 *
 * Wrap staff routes (case manager, compliance, admin) with <RealtimeProvider>
 * to enable real-time event subscriptions via useRealtime().
 */
import React, { createContext, useContext } from 'react';
import { useRealtimeEvents, ConnectionState, WsEvent, WsEventType } from '../hooks/useRealtimeEvents';

interface RealtimeContextValue {
  subscribe: (eventType: WsEventType | '*', callback: (event: WsEvent) => void) => () => void;
  connectionState: ConnectionState;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { subscribe, connectionState } = useRealtimeEvents();

  return (
    <RealtimeContext.Provider value={{ subscribe, connectionState }}>
      {children}
    </RealtimeContext.Provider>
  );
}

/**
 * Hook to access the realtime event subscription API.
 * Must be used within a <RealtimeProvider>.
 * Returns null if no provider is present (safe for customer routes).
 */
export function useRealtime(): RealtimeContextValue | null {
  return useContext(RealtimeContext);
}
